import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { ClinicTreatmentPlan } from "@customTypes/clinic";
import { Pagination } from "@customTypes/index";

import { clinicQKeys } from "./queryKeys";


interface QueryProps {
  pagination?: Partial<Pagination>;
  search?: string;
  isEnabled?: boolean;
}

const queryFn = ({ pagination, search }: QueryProps) => {
  return api.get("clinic/treatment-services", {
    params: {
      ...(pagination && {
        page: pagination.page,
        page_size: pagination.pageSize,
      }),
      ...(search && { search }),
    },
  });
};

const select = (res: any) => {
  // console.log(res.data);

  // Check if response has pagination structure
  if (res.data.data) {
    const pagination: Pagination = {
      page: res.data.pagination.page,
      pageCount: res.data.pagination.page_count,
      pageSize: res.data.pagination.page_size,
      total: res.data.pagination.total,
    };

    const newData: ClinicTreatmentPlan[] = [];

    for (const i in res.data.data) {
      const { name, _id, category, price, created_at, updated_at } = res.data.data[i];

      newData.push({
        id: _id,
        name,
        category: category || "",
        price: price || 0,
        createdAt: new Date(created_at),
        updatedAt: new Date(updated_at),
      });
    }

    return {
      newData,
      pagination,
    };
  }

  // Fallback for non-paginated response
  if (res.data.length) {
    const newData: ClinicTreatmentPlan[] = [];

    for (const i in res.data.reverse()) {
      const { name, _id, category, price, created_at, updated_at } = res.data[i];

      newData.push({
        id: _id,
        name,
        category: category || "",
        price: price || 0,
        createdAt: new Date(created_at),
        updatedAt: new Date(updated_at),
      });
    }

    return {
      newData,
      pagination: {
        page: 1,
        pageCount: 1,
        pageSize: 25, // Use consistent default pageSize instead of newData.length
        total: newData.length,
      },
    };
  }

  return {
    newData: [],
    pagination: {
      page: 1,
      pageCount: 1,
      pageSize: 25, // Use consistent default pageSize instead of 0
      total: 0,
    },
  };
};

export const useGetClinicTreatmentPlansDetailed = ({
  pagination,
  search,
  isEnabled = true,
}: QueryProps = {}) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: [
      ...clinicQKeys.treatmentPlansDetailed(),
      { pagination, search },
    ],
    queryFn: () => queryFn({ pagination, search }),
    select,
    staleTime: Infinity,
    enabled: isEnabled,

    onError: (res: any) => {
      console.error("[From Get All Clinic Treatment Plans Detailed]", res);
      handleErrors(res, "query");
    },
  });
};
